import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';
import { BalanceBanner, TransactionsTable, formatDateTime, formatMoney } from './billingShared';

export default function TeacherStudentBillingPage() {
  const { studentId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [rate, setRate] = useState('');
  const [deposit, setDeposit] = useState('');
  const [depositComment, setDepositComment] = useState('Ручное пополнение');
  const [saving, setSaving] = useState('');

  const [topic, setTopic] = useState('');
  const [description, setDescription] = useState('');
  const [homework, setHomework] = useState('');
  const [minutes, setMinutes] = useState('60');
  const [previewAmount, setPreviewAmount] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/payments/teacher/students/${studentId}/`);
      setData(res.data);
      setRate(res.data.rate_per_hour || '');
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить счёт ученика.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const computed = useMemo(() => {
    const r = Number(rate);
    const m = Number(minutes);
    if (!Number.isFinite(r) || !Number.isFinite(m) || m <= 0 || r <= 0) return '';
    return ((r * m) / 60).toFixed(2);
  }, [rate, minutes]);

  useEffect(() => {
    setPreviewAmount(computed);
  }, [computed]);

  async function saveRate(e) {
    e.preventDefault();
    setSaving('rate');
    setError('');
    try {
      await api.post('/payments/teacher/rates/', {
        student_id: Number(studentId),
        rate_per_hour: rate,
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить ставку.');
    } finally {
      setSaving('');
    }
  }

  async function saveDeposit(e) {
    e.preventDefault();
    setSaving('deposit');
    setError('');
    try {
      await api.post('/payments/teacher/deposit/', {
        student_id: Number(studentId),
        amount: deposit,
        description: depositComment,
      });
      setDeposit('');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось зачислить.');
    } finally {
      setSaving('');
    }
  }

  async function createReport(e) {
    e.preventDefault();
    setSaving('report');
    setError('');
    try {
      await api.post('/payments/teacher/lesson-reports/', {
        student_id: Number(studentId),
        topic,
        description,
        homework,
        duration_minutes: Number(minutes),
      });
      setTopic('');
      setDescription('');
      setHomework('');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить занятие.');
    } finally {
      setSaving('');
    }
  }

  async function cancelReport(id) {
    setSaving(`cancel-${id}`);
    try {
      await api.patch(`/payments/teacher/lesson-reports/${id}/`, { status: 'cancelled' });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось отменить.');
    } finally {
      setSaving('');
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка…</p>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <div className="page-header">
        <p className="text-secondary">
          <Link to="/teacher/billing">← Все ученики</Link>
        </p>
        <h1>{data?.display_name || data?.username || 'Ученик'}</h1>
        <p className="page-subtitle">Семейный счёт, ставка, журнал занятий.</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      <BalanceBanner data={data} />

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Всего внесено</div>
          <div className="stat-value">{formatMoney(data?.total_deposited)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Последний платёж</div>
          <div className="stat-value">{formatDateTime(data?.last_payment_at)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Ставка</div>
          <div className="stat-value">
            {data?.rate_per_hour ? `${formatMoney(data.rate_per_hour)} / час` : 'не задана'}
          </div>
        </div>
      </div>

      {data?.members?.length > 0 && (
        <p className="text-secondary">
          Счёт: {data.members.map((m) => m.display_name || m.username).join(', ')}
        </p>
      )}

      <section className="billing-section billing-forms">
        <form onSubmit={saveRate} className="billing-form-card">
          <h2>Ставка за час</h2>
          <label>
            ₽ / 60 минут
            <input
              className="form-input"
              type="number"
              min="1"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="btn btn-outline" disabled={saving === 'rate'}>
            Сохранить ставку
          </button>
        </form>

        <form onSubmit={saveDeposit} className="billing-form-card">
          <h2>Ручное пополнение</h2>
          <label>
            Сумма, ₽
            <input
              className="form-input"
              type="number"
              step="0.01"
              value={deposit}
              onChange={(e) => setDeposit(e.target.value)}
              required
            />
          </label>
          <label>
            Комментарий
            <input
              className="form-input"
              value={depositComment}
              onChange={(e) => setDepositComment(e.target.value)}
            />
          </label>
          <button type="submit" className="btn btn-outline" disabled={saving === 'deposit'}>
            Зачислить
          </button>
        </form>
      </section>

      <section className="billing-section">
        <h2>Оформить занятие</h2>
        <form onSubmit={createReport} className="billing-form-card billing-form-card--wide">
          <label>
            Тема
            <input className="form-input" value={topic} onChange={(e) => setTopic(e.target.value)} />
          </label>
          <label>
            Что делали
            <textarea className="form-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label>
            Домашнее задание
            <textarea className="form-input" rows={2} value={homework} onChange={(e) => setHomework(e.target.value)} />
          </label>
          <label>
            Длительность, мин
            <input
              className="form-input"
              type="number"
              min="1"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              required
            />
          </label>
          <p>
            К списанию после подтверждения учеником:{' '}
            <strong>{previewAmount ? formatMoney(previewAmount) : '—'}</strong>
          </p>
          <button type="submit" className="btn btn-primary" disabled={saving === 'report'}>
            Сохранить и отправить ученику
          </button>
        </form>
      </section>

      <section className="billing-section">
        <h2>Журнал занятий</h2>
        {!data?.lesson_reports?.length ? (
          <p className="text-secondary">Пока нет записей.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Тема</th>
                <th>Минуты</th>
                <th>Сумма</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.lesson_reports.map((r) => (
                <tr key={r.id}>
                  <td>{formatDateTime(r.created_at)}</td>
                  <td>{r.topic || '—'}</td>
                  <td>{r.duration_minutes}</td>
                  <td>{formatMoney(r.amount)}</td>
                  <td>{r.status_label}</td>
                  <td>
                    {r.status === 'awaiting_student' && (
                      <button
                        type="button"
                        className="btn btn-xs btn-outline"
                        disabled={saving === `cancel-${r.id}`}
                        onClick={() => cancelReport(r.id)}
                      >
                        Отменить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="billing-section">
        <h2>История операций</h2>
        <TransactionsTable items={data?.transactions} />
      </section>
    </div>
  );
}
